import { VizConfig } from "@/shared/config/VizConfig";
import { LayoutDefinitionBuilder, LayoutRecord } from "@/shared/domain/layouts";
import { LayoutDefinition } from "@/shared/domain/layouts/LayoutDefinition";
import { LoggerService } from "@/shared/services";
import { type VizStateChangeSet } from "@/shared/state/visualization/diffVizState";
import { PanelState } from "@/shared/state/visualization/schema";

/**
 * Applies panel changes to an existing layout definition
 */
export function applyChangesToLayout(
  existingLayoutDefinition: LayoutRecord["layout_definition"],
  schemaVersion: LayoutRecord["schema_version"],
  diff: VizStateChangeSet,
): LayoutDefinition {
  LoggerService.log("Applying changes to layout:", { diff });
  LoggerService.log("Existing layout:", existingLayoutDefinition);

  // Parse the existing layout definition
  const oldLayoutDefinition = LayoutDefinition.from_obj(
    existingLayoutDefinition,
    schemaVersion,
  );

  // If there are no changes, return the existing layout definition
  if (!diff.hasChanges) {
    return oldLayoutDefinition;
  }

  const builder = new LayoutDefinitionBuilder(oldLayoutDefinition);

  // Handle removed panels
  Object.keys(diff.panels.removed).forEach((panelId) => {
    builder.removePanel(panelId);
  });

  // Handle changed panels
  Object.entries(diff.panels.changed).forEach(([panelId, panelState]) => {
    const { data: _, ...panelMetadata } = panelState;
    const ruleIds = addNewRules(builder, panelState);
    builder.updatePanel(panelId, panelMetadata, ruleIds);
  });

  // Handle added panels
  Object.entries(diff.panels.added).forEach(([_, panelState]) => {
    const { data: __, ...panelMetadata } = panelState;
    const ruleIds = addNewRules(builder, panelState);
    builder.addPanel(panelMetadata, ruleIds);
  });

  if (diff.layout.changed) {
    builder.setLayout(diff.layout.changed);
  }

  return builder.build();
}

function addNewRules(
  builder: LayoutDefinitionBuilder,
  panelState: PanelState,
): string[] {
  const panelDataAndStyles = VizConfig.getDataAndStylesForPanel(panelState);

  return panelDataAndStyles.map(({ data, style }) => {
    const requirementSet = data.map((topicData) =>
      LayoutDefinitionBuilder.createRequirementFromTopicData(topicData),
    );
    return builder.addRule(requirementSet, style);
  });
}
